import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import authApi from '../api/auth';
import storage from '../utils/storage';

export const AuthContext = createContext(null);

const STORAGE_KEY = 'hschool.session';
const TOKEN_KEY = 'hschool.token';

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    token: null,
    tokenExpiresAt: null,
    rememberMe: false,
    loading: true,
  });

  const applySession = useCallback((session) => {
    if (session?.token && session?.user) {
      storage.setJSON(STORAGE_KEY, session);
      localStorage.setItem(TOKEN_KEY, session.token);
      try {
        sessionStorage.setItem('token', session.token);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[auth] Failed to persist session token', err);
      }
      try {
        localStorage.setItem('token', session.token);
        localStorage.setItem('user', JSON.stringify(session.user));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[auth] Failed to persist legacy auth data', err);
      }
      setAuthState({
        user: session.user,
        token: session.token,
        tokenExpiresAt: session.tokenExpiresAt || null,
        rememberMe: Boolean(session.rememberMe),
        loading: false,
      });
    } else {
      storage.remove(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      try {
        sessionStorage.removeItem('token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[auth] Failed to clear legacy auth data', err);
      }
      setAuthState({
        user: null,
        token: null,
        tokenExpiresAt: null,
        rememberMe: false,
        loading: false,
      });
    }
  }, []);

  useEffect(() => {
    const stored = storage.getJSON(STORAGE_KEY);
    if (stored?.token && stored?.user) {
      applySession(stored);
    } else {
      setAuthState((prev) => ({ ...prev, loading: false }));
    }
  }, [applySession]);

  const login = useCallback(async (credentials) => {
    const data = await authApi.login(credentials);
    applySession({
      token: data.token,
      user: data.user,
      tokenExpiresAt: data.tokenExpiresAt,
      rememberMe: data.rememberMe,
    });
    return data;
  }, [applySession]);

  const logout = useCallback(() => {
    applySession(null);
  }, [applySession]);

  const updateUser = useCallback((nextUser) => {
    setAuthState((prev) => {
      if (!prev.token) return prev;
 const mergedUser = typeof nextUser === 'function' ? nextUser(prev.user) : nextUser;
      const session = {
        token: prev.token,
        user: mergedUser,
        tokenExpiresAt: prev.tokenExpiresAt,
        rememberMe: prev.rememberMe,
      };
      storage.setJSON(STORAGE_KEY, session);
      try {
        localStorage.setItem('user', JSON.stringify(mergedUser));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[auth] Failed to persist legacy user data', err);
      }
      return {
        ...prev,
        user: mergedUser,
      };
    });
  }, []);

  const value = useMemo(() => ({
    user: authState.user,
    token: authState.token,
    tokenExpiresAt: authState.tokenExpiresAt,
    rememberMe: authState.rememberMe,
    loading: authState.loading,
    role: authState.user?.role || null,
    isAuthenticated: Boolean(authState.token && authState.user),
    login,
    logout,
    updateUser,
    setUser: updateUser,
  }), [authState.loading, authState.rememberMe, authState.token, authState.tokenExpiresAt, authState.user, login, logout, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};